import { useId, useState } from "react";
import { ConfirmDialog } from "@common/ConfirmDialog";
import { ComponentColor } from "@common/types";

// TODO: adopt @admin/_components/ReasonField (and its REASON_MAX_LENGTH) once PR 325 and PR 332 have both merged.
// The API's cap on an OrderStatusHistory note, where the reason is stored
// (DomainConstraint.OrderStatusHistory.Notes.MaxLength).
const REASON_MAX_LENGTH = 500;

interface CancelOrderDialogProps {
    orderNumber: string;
    isProcessing: boolean;
    // Inline failure copy from the parent's request. The dialog stays open on
    // failure so the admin can read it and retry with the same reason.
    errorMessage: string | null;
    onConfirm: (reason: string) => void;
    onClose: () => void;
}

// Admin cancel (#452 / AO-08). Each consequence below is what the API does on
// POST /orders/{id}/transition to Cancelled: the status is terminal, the save
// voids the order's invoices, nothing is refunded (out of MVP) and no email
// is sent. The reason is required and stored as the status-history note.
export const CancelOrderDialog = ({
    orderNumber,
    isProcessing,
    errorMessage,
    onConfirm,
    onClose,
}: CancelOrderDialogProps) => {
    const [reason, setReason] = useState("");
    const reasonId = useId();

    const trimmed = reason.trim();

    return (
        <ConfirmDialog
            title={`Cancel order ${orderNumber}?`}
            body={
                <>
                    <p className="mb-2">Cancelling can't be undone.</p>
                    <ul className="list-disc space-y-1 pl-5">
                        <li>The order's invoice is voided.</li>
                        <li>
                            The payment is not refunded. If it was captured,
                            refund it in Authorize.Net.
                        </li>
                        <li>The customer is not emailed.</li>
                    </ul>
                </>
            }
            confirmLabel="Cancel order"
            confirmColor={ComponentColor.Error}
            // The safe back-out keeps the order, so a stray Enter on open
            // keeps it too.
            dismissLabel="Keep order"
            isProcessing={isProcessing}
            confirmDisabled={trimmed.length === 0}
            errorMessage={errorMessage}
            // A stray click outside the panel must not throw away the typed
            // (required) reason.
            closeOnBackdropClick={false}
            onConfirm={() => onConfirm(trimmed)}
            onClose={onClose}
        >
            <label htmlFor={reasonId} className="mb-1 block text-sm font-semibold">
                Reason for cancelling
            </label>
            <textarea
                id={reasonId}
                className="textarea textarea-bordered w-full"
                rows={3}
                value={reason}
                maxLength={REASON_MAX_LENGTH}
                disabled={isProcessing}
                onChange={(event) => setReason(event.target.value)}
            />
            <p className="mb-4 mt-1 text-xs text-neutral/70">
                Saved in the order's status history. The customer can see it.
            </p>
        </ConfirmDialog>
    );
};
